import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  CircularProgress,
  LinearProgress,
  InputAdornment,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@tanstack/react-query';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { getAuditLogs } from '../api';
import {
  areAuditTrailEntriesConsistentWithSearch,
  filterAuditTrailEntries,
  getAuditLogLookupParams,
  mapAuditLogsToTrailEntries,
  type AuditTrailChange,
  type AuditTrailEntry,
} from '../utils/auditTrail';

export {
  areAuditTrailEntriesConsistentWithSearch,
  auditTrailEntryMatchesSearch,
  filterAuditTrailEntries,
  getAuditLogLookupParams,
  getAuditTrailEntrySearchValues,
  mapAuditLogsToTrailEntries,
} from '../utils/auditTrail';
export type { AuditTrailChange, AuditTrailEntry } from '../utils/auditTrail';

type AuditTrailWidgetProps = {
  entries?: AuditTrailEntry[];
  className?: string;
  previewCount?: number;
  pinFields?: string[];
  height?: string;
};

const SEARCH_DEBOUNCE_MS = 350;

const formatDate = (d: string | number | Date) => {
  const date = new Date(d);

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

type ActionChipProps = {
  action: AuditTrailEntry['action'];
};

const ActionChip = ({ action }: ActionChipProps) => {
  const config = {
    create: {
      icon: <AddIcon sx={{ fontSize: 16 }} />,
      label: 'Created',
      color: 'primary' as const,
    },
    update: {
      icon: <EditIcon sx={{ fontSize: 16 }} />,
      label: 'Updated',
      color: 'warning' as const,
    },
    delete: {
      icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />,
      label: 'Deleted',
      color: 'error' as const,
    },
  }[action];

  return (
    <Chip
      size="small"
      variant="outlined"
      color={config.color}
      icon={config.icon}
      label={
        <Box component="span" sx={{ fontSize: 12, color: 'text.primary' }}>
          {config.label}
        </Box>
      }
      sx={{ borderRadius: 999, height: 24, px: 0.5 }}
    />
  );
};

type StatusPillProps = {
  status: AuditTrailChange['status'];
};

const StatusPill = ({ status }: StatusPillProps) => {
  const map = {
    added: { color: 'success', text: 'Added' },
    updated: { color: 'warning', text: 'Updated' },
    removed: { color: 'error', text: 'Removed' },
  } as const;

  const cfg = map[status];

  return (
    <Chip
      size="small"
      label={cfg.text}
      sx={{
        height: 20,
        borderRadius: 999,
        fontSize: 10,
        textTransform: 'uppercase',
      }}
      variant="outlined"
      color={cfg.color as any}
    />
  );
};

type ValueChipProps = {
  value?: string;
  muted?: boolean;
};

const ValueChip = ({ value, muted }: ValueChipProps) => {
  if (!value) {
    return (
      <Chip
        size="small"
        variant={muted ? 'outlined' : 'filled'}
        label="—"
        sx={{ borderRadius: 1 }}
      />
    );
  }

  const isMultiline = value.includes('\n');

  if (isMultiline) {
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          px: 1.25,
          py: 1,
          borderRadius: 1,
          border: '1px solid',
          borderColor: muted ? 'divider' : 'primary.light',
          bgcolor: muted ? 'background.paper' : 'action.hover',
          color: 'text.primary',
          fontSize: 12,
          lineHeight: 1.4,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
          maxWidth: { xs: '100%', sm: 520 },
          overflowX: 'auto',
        }}
      >
        {value}
      </Box>
    );
  }

  return (
    <Chip
      size="small"
      variant={muted ? 'outlined' : 'filled'}
      label={value}
      sx={{
        maxWidth: 260,
        borderRadius: 1,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
        '.MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
      }}
    />
  );
};

type ChangeRowProps = {
  change: AuditTrailChange;
};

const ChangeRow = ({ change }: ChangeRowProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '120px 1fr', sm: '140px 1fr' },
      alignItems: 'center',
      gap: 1,
      py: 0.5,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <StatusPill status={change.status} />
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, width: 120, color: 'text.secondary' }}
      >
        {change.field}
      </Typography>
    </Stack>
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ minWidth: 0, marginLeft: 40 }}
    >
      {change.status === 'added' ? (
        <ValueChip value={change.after} />
      ) : change.status === 'removed' ? (
        <ValueChip value={change.before} />
      ) : (
        <>
          <ValueChip value={change.before} muted />
          <ArrowRightAltIcon fontSize="small" sx={{ opacity: 0.6 }} />
          <ValueChip value={change.after} />
        </>
      )}
    </Stack>
  </Box>
);

type EntryAccordionProps = {
  entry: AuditTrailEntry;
  previewCount: number;
  pinFields: string[];
};

const EntryAccordion = ({
  entry,
  previewCount,
  pinFields,
}: EntryAccordionProps) => {
  const sortedChanges = useMemo(() => {
    const list = entry.changes ?? [];

    if (!pinFields.length) {
      return list;
    }

    const priority = new Map(pinFields.map((field, index) => [field, index]));

    return [...list].sort(
      (a, b) =>
        (priority.get(a.field) ?? Number.MAX_SAFE_INTEGER) -
        (priority.get(b.field) ?? Number.MAX_SAFE_INTEGER)
    );
  }, [entry.changes, pinFields]);

  const preview = sortedChanges.slice(0, previewCount);
  const rest = sortedChanges.slice(previewCount);

  return (
    <Accordion
      disableGutters
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 44,
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <ActionChip action={entry.action} />
          {entry.summary ? (
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {entry.summary}
            </Typography>
          ) : null}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              color: 'text.secondary',
            }}
          >
            {preview.length > 0 ? (
              <Typography variant="caption" noWrap>
                {preview.map((change) => change.field).join(', ')}
                {rest.length ? ` (+${rest.length})` : ''}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ color: 'text.secondary' }}>
          {entry.actorName ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PersonOutlineIcon fontSize="small" />
              <Typography variant="caption">{entry.actorName}</Typography>
            </Stack>
          ) : null}
          {entry.actorEmail ? (
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <MailOutlineIcon fontSize="small" />
              <Typography variant="caption">{entry.actorEmail}</Typography>
            </Box>
          ) : null}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccessTimeIcon fontSize="small" />
            <Typography variant="caption">
              {formatDate(entry.createdAt)}
            </Typography>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Divider sx={{ mb: 1 }} />
        <Box>
          {sortedChanges.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
              No field-level changes recorded for this event.
            </Typography>
          ) : (
            sortedChanges.map((change, index) => (
              <Box key={`${change.field}-${index}`} sx={{ py: 0.75 }}>
                <ChangeRow change={change} />
                {index < sortedChanges.length - 1 ? (
                  <Divider sx={{ mt: 0.75 }} />
                ) : null}
              </Box>
            ))
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const EmptyState = ({
  message = 'No audit entries found',
}: {
  message?: string;
}) => (
  <Box
    sx={{
      height: 160,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 1,
      color: 'text.secondary',
      textAlign: 'center',
    }}
  >
    <SearchIcon fontSize="small" />
    <Typography variant="body2">{message}</Typography>
  </Box>
);

export default function AuditTrailWidget({
  entries,
  className,
  previewCount = 4,
  pinFields = ['id', 'date', 'time', 'uuid', 'user_id'],
  height = '24rem',
}: AuditTrailWidgetProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [field, setField] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const trimmedQuery = debouncedQuery.trim();
  const lookupParams = useMemo(
    () => getAuditLogLookupParams(trimmedQuery),
    [trimmedQuery]
  );
  const shouldFetch = entries === undefined;
  const isRemoteSearchMode = shouldFetch && Boolean(trimmedQuery || field);
  const { errorMsg } = useSnackbarMsg();

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [query]);

  const {
    data: remoteData,
    isLoading: isRemoteLoading,
    isFetching: isRemoteFetching,
  } = useQuery({
    queryKey: ['audit-logs', 'timeline', page, pageSize, trimmedQuery, field],
    queryFn: () => {
      const params = {
        ...lookupParams,
        sortBy: 'created_at',
        sortOrder: 'desc',
      };

      return getAuditLogs({
        page,
        limit: pageSize,
        ...params,
      });
    },
    keepPreviousData: true,
    enabled: shouldFetch,
    onError: (error) => {
      errorMsg(error, 'Unable to load audit logs');
    },
  });

  const remoteEntries = useMemo(() => {
    if (!remoteData) {
      return [];
    }

    return mapAuditLogsToTrailEntries(remoteData.data ?? []);
  }, [remoteData]);

  const safeEntries = useMemo(
    () =>
      entries === undefined
        ? remoteEntries
        : Array.isArray(entries)
        ? entries
        : [],
    [entries, remoteEntries]
  );
  const meta = remoteData?.meta ?? null;
  const isServerResultConsistentWithFilters =
    !isRemoteSearchMode ||
    isRemoteLoading ||
    safeEntries.length === 0 ||
    areAuditTrailEntriesConsistentWithSearch({
      entries: safeEntries,
      query: trimmedQuery,
      field,
    });

  const allFields = useMemo(() => {
    const set = new Set<string>();

    safeEntries.forEach((entry) => {
      entry.changes?.forEach((change) => {
        set.add(change.field);
      });
    });

    return Array.from(set).sort();
  }, [safeEntries]);

  const filteredEntries = useMemo(() => {
    if (entries === undefined && isRemoteLoading) {
      return safeEntries;
    }

    if (entries === undefined && isServerResultConsistentWithFilters) {
      return safeEntries;
    }

    if (entries === undefined) {
      return safeEntries;
    }

    return filterAuditTrailEntries(safeEntries, trimmedQuery, field);
  }, [
    safeEntries,
    trimmedQuery,
    field,
    entries,
    isRemoteLoading,
    isServerResultConsistentWithFilters,
  ]);

  const totalItems =
    entries === undefined
      ? meta?.total ?? filteredEntries.length
      : filteredEntries.length;
  const calculatedTotalPages =
    totalItems > 0 && pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;
  const totalPages =
    entries === undefined
      ? meta?.totalPages ?? calculatedTotalPages
      : calculatedTotalPages;
  const shouldShowPagination = entries === undefined && totalItems > pageSize;
  const visibleEntries = filteredEntries;
  const shouldShowEmptyState =
    visibleEntries.length === 0 && !(shouldFetch && isRemoteFetching);
  const shouldShowUnfilteredSearchWarning =
    isRemoteSearchMode &&
    !isRemoteLoading &&
    !isRemoteFetching &&
    !isServerResultConsistentWithFilters;

  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, shouldFetch, totalPages]);

  return (
    <Card className={className} sx={{ width: '100%' }}>
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ fontSize: 16 }}>
              Audit trail
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={totalItems}
              sx={{ borderRadius: 999 }}
            />
          </Stack>
        }
        sx={{ pb: 1.5 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mb: 1.5 }}
        >
          <TextField
            size="small"
            fullWidth
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search actor, session/resource ID, field, value..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            select
            value={field}
            onChange={(event) => {
              setField(event.target.value);
              setPage(1);
            }}
            placeholder="All fields"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: '100%', sm: 220 } }}
          >
            <MenuItem value="">All fields</MenuItem>
            {allFields.map((fieldOption) => (
              <MenuItem key={fieldOption} value={fieldOption}>
                {fieldOption}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Divider />

        {shouldFetch && isRemoteFetching ? (
          <LinearProgress sx={{ mt: 1, mb: 1 }} />
        ) : null}

        {shouldShowUnfilteredSearchWarning ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Search filters were not applied by the audit logs API.
          </Alert>
        ) : null}

        <Box sx={{ pt: 1, maxHeight: height, overflow: 'auto', pr: 0.5 }}>
          {shouldFetch && isRemoteLoading ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading audit trail…
                </Typography>
              </Stack>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {shouldShowEmptyState ? <EmptyState /> : null}
              {visibleEntries.map((entry) => (
                <EntryAccordion
                  key={entry.id}
                  entry={entry}
                  previewCount={previewCount}
                  pinFields={pinFields}
                />
              ))}
            </Stack>
          )}
        </Box>

        {shouldShowPagination ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <TextField
              select
              size="small"
              label="Rows per page"
              value={pageSize}
              onChange={(event) => {
                const next = Number(event.target.value);
                setPageSize(next);
                setPage(1);
              }}
              sx={{ width: { xs: '100%', sm: 180 } }}
            >
              {[10, 20, 50].map((option) => (
                <MenuItem key={option} value={option}>
                  {option} per page
                </MenuItem>
              ))}
            </TextField>
            <Pagination
              color="primary"
              count={Math.max(totalPages, 1)}
              page={page}
              onChange={(_event, value) => setPage(value)}
              size="small"
              sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
            />
          </Stack>
        ) : null}
      </CardContent>
    </Card>
  );
}
